import { Component, inject, OnInit } from '@angular/core'
import { CommonModule, DatePipe } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { TableLazyLoadEvent, TableModule } from 'primeng/table'
import { CardModule } from 'primeng/card'
import { ButtonModule } from 'primeng/button'
import { DropdownModule } from 'primeng/dropdown'
import { InputTextModule } from 'primeng/inputtext'
import { TagModule } from 'primeng/tag'
import { TooltipModule } from 'primeng/tooltip'
import { DialogModule } from 'primeng/dialog'
import { LogService, LogEntry, LogFile } from '../../services/log.service'

@Component({
  selector: 'app-logs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    CardModule,
    ButtonModule,
    DropdownModule,
    InputTextModule,
    TagModule,
    TooltipModule,
    DialogModule,
    DatePipe,
  ],
  templateUrl: './logs.component.html',
  styleUrl: './logs.component.scss',
})
export class LogsComponent implements OnInit {
  private logService = inject(LogService)

  public files$ = this.logService.files$
  public entries$ = this.logService.entries$
  public total$ = this.logService.total$
  public loading$ = this.logService.loading$
  public filesLoading$ = this.logService.filesLoading$

  public selectedFile = 'laravel.log'
  public filterLevel = ''
  public filterSearch = ''

  public selectedEntry: LogEntry | null = null
  public showDetailDialog = false

  public levelOptions = [
    { label: 'Tous les niveaux', value: '' },
    { label: 'Emergency', value: 'emergency' },
    { label: 'Alert', value: 'alert' },
    { label: 'Critical', value: 'critical' },
    { label: 'Error', value: 'error' },
    { label: 'Warning', value: 'warning' },
    { label: 'Notice', value: 'notice' },
    { label: 'Info', value: 'info' },
    { label: 'Debug', value: 'debug' },
  ]

  private currentPage = 1
  private currentPerPage = 50

  ngOnInit() {
    this.logService.getFiles().subscribe((res) => {
      if (res.data.length > 0) {
        this.selectedFile = res.data[0].name
        this.loadEntries()
      }
    })
  }

  loadEntries(page = 1, perPage = 50) {
    this.currentPage = page
    this.currentPerPage = perPage
    this.logService
      .getEntries(
        this.selectedFile,
        page,
        perPage,
        this.filterLevel || undefined,
        this.filterSearch || undefined
      )
      .subscribe()
  }

  onFileChange() {
    this.loadEntries()
  }

  onFilter() {
    this.loadEntries()
  }

  onReset() {
    this.filterLevel = ''
    this.filterSearch = ''
    this.loadEntries()
  }

  onLazyLoad(event: TableLazyLoadEvent) {
    const page =
      event.first !== undefined && event.rows
        ? Math.floor(event.first / event.rows) + 1
        : 1
    const perPage = event.rows ?? 50
    this.loadEntries(page, perPage)
  }

  openDetail(entry: LogEntry) {
    this.selectedEntry = entry
    this.showDetailDialog = true
  }

  getLevelSeverity(level: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch (level.toLowerCase()) {
      case 'emergency':
      case 'alert':
      case 'critical':
      case 'error':
        return 'danger'
      case 'warning':
        return 'warn'
      case 'notice':
      case 'info':
        return 'info'
      case 'debug':
        return 'secondary'
      default:
        return 'secondary'
    }
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / 1048576).toFixed(1)} MB`
  }
}
